import { formatBytes, packTotalBytes, type PackDescriptor } from "../storage/manifest";
import { MapShareReceiver } from "../sharing/mapTransfer";
import type { PeerStatus } from "../sharing/peerLink";

interface ImportScreenOptions {
  key: Uint8Array<ArrayBuffer>;
  onDone: () => void;
  onCancel: () => void;
}

export class ImportScreen {
  private readonly root = document.createElement("div");
  private readonly status = document.createElement("p");
  private readonly details = document.createElement("div");
  private readonly progress = document.createElement("progress");
  private readonly progressText = document.createElement("div");
  private readonly done = document.createElement("button");
  private receiver: MapShareReceiver | null = null;
  private finished = false;

  constructor(private readonly options: ImportScreenOptions) {
    this.root.className = "setup";
    this.render();
  }

  get element(): HTMLElement {
    return this.root;
  }

  async start(): Promise<void> {
    this.receiver = new MapShareReceiver(
      this.options.key,
      {
        onStatus: (status) => this.showPeerStatus(status),
        onProgress: (pack, received, total) => this.showProgress(pack, received, total),
        onError: (error) => this.showError(error),
        onComplete: () => this.showComplete(),
      },
      (packs) => this.showManifest(packs),
    );
    await this.receiver.start().catch((error: unknown) =>
      this.showError(error instanceof Error ? error : new Error("Could not receive the map.")),
    );
  }

  private render(): void {
    const inner = document.createElement("div");
    inner.className = "setup-inner share-screen";
    const header = document.createElement("header");
    const title = document.createElement("h1");
    title.textContent = "Receive a shared map";
    const lede = document.createElement("p");
    lede.className = "lede";
    lede.textContent =
      "Keep this screen open. The encrypted map travels directly from the other device and is stored for offline use.";
    header.append(title, lede);

    this.status.className = "notice transfer-status";
    this.status.textContent = "Connecting to the signaling service…";
    this.details.className = "storage incoming-details";
    this.details.hidden = true;
    this.progress.className = "transfer-progress";
    this.progress.max = 1;
    this.progress.value = 0;
    this.progressText.className = "pack-note";
    this.progressText.textContent = "Waiting for map details";
    this.details.append(this.progress, this.progressText);

    const actions = document.createElement("div");
    actions.className = "actions";
    this.done.className = "button";
    this.done.type = "button";
    this.done.textContent = "Open received map";
    this.done.hidden = true;
    this.done.addEventListener("click", () => {
      void this.receiver?.stop().then(() => this.options.onDone());
    });
    const cancel = document.createElement("button");
    cancel.className = "button secondary";
    cancel.type = "button";
    cancel.textContent = "Cancel receiving";
    cancel.addEventListener("click", () => void this.cancel());
    actions.append(this.done, cancel);

    inner.append(header, this.status, this.details, actions);
    this.root.replaceChildren(inner);
  }

  private showManifest(packs: PackDescriptor[]): void {
    this.details.hidden = false;
    const existingRows = [...this.details.querySelectorAll(".storage-row")];
    for (const row of existingRows) row.remove();
    const total = packs.reduce((sum, pack) => sum + packTotalBytes(pack), 0);
    const chart = packs.find((pack) => !pack.required) ?? packs[0];
    for (const [label, value] of [
      ["Incoming map", chart.label],
      ["Package size", formatBytes(total)],
    ]) {
      const row = document.createElement("div");
      row.className = "storage-row";
      const left = document.createElement("span");
      left.textContent = label;
      const right = document.createElement("span");
      right.textContent = value;
      row.append(left, right);
      this.details.insertBefore(row, this.progress);
    }
  }

  private showPeerStatus(status: PeerStatus | "transferring" | "complete"): void {
    if (this.finished || status === "transferring") return;
    const text: Record<PeerStatus | "complete", string> = {
      signaling: "Connecting to the signaling service…",
      waiting: "Waiting for the sending device…",
      connecting: "Sender found. Establishing a direct connection…",
      connected: "Connected. Waiting for map details…",
      disconnected: "The sending device disconnected.",
      complete: "Map received successfully.",
    };
    this.status.textContent = text[status];
  }

  private showProgress(pack: PackDescriptor, received: number, total: number): void {
    this.details.hidden = false;
    this.status.textContent = "Receiving and saving map data…";
    this.progress.value = total ? received / total : 0;
    this.progressText.textContent = `${pack.label}: ${formatBytes(received)} of ${formatBytes(total)}`;
  }

  private showComplete(): void {
    this.finished = true;
    this.progress.value = 1;
    this.status.dataset.tone = "success";
    this.status.textContent = "Map received and saved for offline use.";
    this.done.hidden = false;
    const cancel = this.done.nextElementSibling as HTMLButtonElement | null;
    if (cancel) cancel.textContent = "Close";
  }

  private showError(error: Error): void {
    this.status.dataset.tone = "error";
    this.status.textContent = error.message;
  }

  private async cancel(): Promise<void> {
    await this.receiver?.stop();
    this.options.onCancel();
  }
}
